import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ServerIcon,
  CloudIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  CodeBracketIcon,
  CommandLineIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  FolderIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  WrenchIcon,
  CreditCardIcon,
  KeyIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface Step {
  id: string;
  category: string;
  title: string;
  description: string;
  commands?: string[];
  duration: string;
  cost: string;
  required: boolean;
  status: 'pending' | 'in-progress' | 'completed' | 'skipped';
  dependencies?: string[];
  outputs?: string[];
  icon: React.ComponentType<any>;
}

export const CompleteImplementationSteps: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const categories = [
    { id: 'all', name: 'All Steps', color: 'gray' },
    { id: 'accounts', name: '1. Accounts & Access', color: 'purple' },
    { id: 'infrastructure', name: '2. Infrastructure', color: 'blue' },
    { id: 'backend', name: '3. Backend & API', color: 'green' },
    { id: 'frontend', name: '4. Frontend', color: 'yellow' },
    { id: 'deployment', name: '5. Deployment', color: 'red' },
    { id: 'testing', name: '6. Testing & Launch', color: 'pink' }
  ];

  const steps: Step[] = [
    // ACCOUNTS & ACCESS
    {
      id: 'aws-account',
      category: 'accounts',
      title: 'Create AWS Account',
      description: 'Sign up for AWS to use SES for email sending',
      commands: [
        '# Go to https://aws.amazon.com',
        '# Click "Create an AWS Account"',
        '# Enter email, password, account name',
        '# Add credit card (required)',
        '# Verify phone number',
        '# Choose Basic (free) support plan'
      ],
      duration: '15 min',
      cost: '$0 (Free tier)',
      required: true,
      status: 'pending',
      outputs: ['AWS Account ID', 'Root user credentials'],
      icon: CreditCardIcon
    },
    {
      id: 'cloudflare-account',
      category: 'accounts',
      title: 'Create CloudFlare Account',
      description: 'Sign up for CloudFlare for Workers, R2, and D1',
      commands: [
        '# Go to https://cloudflare.com',
        '# Click "Sign Up"',
        '# Verify email',
        '# Add payment method for R2 storage'
      ],
      duration: '10 min',
      cost: '$0 to start',
      required: true,
      status: 'pending',
      outputs: ['CloudFlare Account ID'],
      icon: CloudIcon
    },
    {
      id: 'domain-purchase',
      category: 'accounts',
      title: 'Buy Domain Name',
      description: 'Purchase domain for your application',
      commands: [
        '# Go to namecheap.com or cloudflare.com/domains',
        '# Search for "happyhumantools.com"',
        '# Add to cart and purchase',
        '# Point nameservers to CloudFlare'
      ],
      duration: '20 min',
      cost: '$10-15/year',
      required: true,
      status: 'pending',
      outputs: ['Domain name', 'DNS access'],
      icon: GlobeAltIcon
    },

    // INFRASTRUCTURE SETUP
    {
      id: 'iam-user',
      category: 'infrastructure',
      title: 'Create IAM User for SES',
      description: 'Set up programmatic access for email sending',
      commands: [
        '# AWS Console → IAM → Users → Add User',
        '# Username: ses-sender',
        '# Access type: Programmatic access',
        '# Attach policy: AmazonSESFullAccess',
        '# Download credentials CSV'
      ],
      duration: '10 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['aws-account'],
      outputs: ['Access Key ID', 'Secret Access Key'],
      icon: KeyIcon
    },
    {
      id: 'ses-setup',
      category: 'infrastructure',
      title: 'Configure AWS SES',
      description: 'Set up email sending service',
      commands: [
        '# AWS Console → SES → Email Addresses',
        '# Add and verify sender email',
        '# AWS Console → SES → Sending Statistics',
        '# Request production access (exit sandbox)',
        '# Wait 24 hours for approval'
      ],
      duration: '30 min + 24h wait',
      cost: '$0.10 per 1000 emails',
      required: true,
      status: 'pending',
      dependencies: ['iam-user'],
      outputs: ['Verified sender email', 'Production access'],
      icon: EnvelopeIcon
    },
    {
      id: 'r2-bucket',
      category: 'infrastructure',
      title: 'Create R2 Storage Bucket',
      description: 'Set up CloudFlare R2 for file storage',
      commands: [
        '# CloudFlare Dashboard → R2',
        '# Create bucket: happyhumantools',
        '# Settings → CORS → Add rule:',
        '# Origins: ["*"]',
        '# Methods: ["GET", "POST", "PUT", "DELETE"]',
        '# Generate R2 API token',
        '# Save credentials'
      ],
      duration: '15 min',
      cost: '$0.015/GB/month',
      required: true,
      status: 'pending',
      dependencies: ['cloudflare-account'],
      outputs: ['Bucket name', 'R2 API credentials', 'Endpoint URL'],
      icon: FolderIcon
    },
    {
      id: 'd1-database',
      category: 'infrastructure',
      title: 'Create D1 Database',
      description: 'Set up CloudFlare D1 for data storage',
      commands: [
        'npx wrangler d1 create happyhumantools-db',
        '# Copy database_id from output',
        '# Update wrangler.toml:',
        '[[d1_databases]]',
        'binding = "DB"',
        'database_name = "happyhumantools-db"',
        'database_id = "your-database-id"'
      ],
      duration: '10 min',
      cost: '$0 (Free tier: 5GB)',
      required: true,
      status: 'pending',
      dependencies: ['cloudflare-account'],
      outputs: ['Database ID', 'Database binding'],
      icon: CircleStackIcon
    },

    // BACKEND & API
    {
      id: 'worker-setup',
      category: 'backend',
      title: 'Create CloudFlare Worker',
      description: 'Set up serverless backend API',
      commands: [
        'npm create cloudflare@latest happyhumantools-api',
        'cd happyhumantools-api',
        'npm install @aws-sdk/client-ses',
        'npm install @aws-sdk/client-s3',
        '# Create src/index.ts with API routes'
      ],
      duration: '30 min',
      cost: '$0 (100k req/day free)',
      required: true,
      status: 'pending',
      dependencies: ['d1-database', 'r2-bucket'],
      outputs: ['Worker project', 'API endpoints'],
      icon: CpuChipIcon
    },
    {
      id: 'api-routes',
      category: 'backend',
      title: 'Implement API Routes',
      description: 'Create all necessary API endpoints',
      commands: [
        '# POST /api/business/create',
        '# POST /api/files/upload',
        '# GET /api/files/:businessSlug',
        '# POST /api/email/send',
        '# GET /api/business/list',
        '# DELETE /api/files/:id'
      ],
      duration: '2 hours',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['worker-setup'],
      outputs: ['Working API endpoints'],
      icon: CodeBracketIcon
    },
    {
      id: 'database-schema',
      category: 'backend',
      title: 'Create Database Schema',
      description: 'Set up D1 database tables',
      commands: [
        'npx wrangler d1 execute happyhumantools-db --local --file=./schema.sql',
        '',
        '-- schema.sql:',
        'CREATE TABLE businesses (',
        '  id TEXT PRIMARY KEY,',
        '  slug TEXT UNIQUE,',
        '  name TEXT,',
        '  created_at DATETIME',
        ');',
        '',
        'CREATE TABLE files (',
        '  id TEXT PRIMARY KEY,',
        '  business_id TEXT,',
        '  name TEXT,',
        '  url TEXT,',
        '  uploaded_at DATETIME',
        ');'
      ],
      duration: '20 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['d1-database'],
      outputs: ['Database tables'],
      icon: CircleStackIcon
    },
    {
      id: 'env-variables',
      category: 'backend',
      title: 'Configure Environment Variables',
      description: 'Set up secrets and environment variables',
      commands: [
        'npx wrangler secret put AWS_ACCESS_KEY_ID',
        'npx wrangler secret put AWS_SECRET_ACCESS_KEY',
        'npx wrangler secret put R2_ACCESS_KEY_ID',
        'npx wrangler secret put R2_SECRET_ACCESS_KEY',
        'npx wrangler secret put SES_FROM_EMAIL'
      ],
      duration: '10 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['worker-setup', 'ses-setup', 'r2-bucket'],
      outputs: ['Configured secrets'],
      icon: ShieldCheckIcon
    },

    // FRONTEND
    {
      id: 'frontend-build',
      category: 'frontend',
      title: 'Build React Application',
      description: 'Compile and optimize frontend',
      commands: [
        'cd apps/web',
        'npm install',
        'npm run build',
        '# Creates dist/ folder with static files'
      ],
      duration: '5 min',
      cost: '$0',
      required: true,
      status: 'pending',
      outputs: ['dist/ folder', 'Static HTML/JS/CSS'],
      icon: CodeBracketIcon
    },
    {
      id: 'frontend-api-integration',
      category: 'frontend',
      title: 'Connect Frontend to API',
      description: 'Update API endpoints in React app',
      commands: [
        '# Update .env:',
        'VITE_API_URL=https://api.happyhumantools.com',
        '',
        '# Update API calls to use real endpoints',
        '# Remove localStorage mock data',
        '# Add error handling'
      ],
      duration: '1 hour',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['api-routes'],
      outputs: ['Connected frontend'],
      icon: ArrowPathIcon
    },

    // DEPLOYMENT
    {
      id: 'deploy-worker',
      category: 'deployment',
      title: 'Deploy Worker to CloudFlare',
      description: 'Push backend API to production',
      commands: [
        'cd happyhumantools-api',
        'npx wrangler publish',
        '# Note the worker URL',
        '# Test endpoints with curl'
      ],
      duration: '5 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['api-routes', 'env-variables', 'database-schema'],
      outputs: ['Live API URL'],
      icon: CloudIcon
    },
    {
      id: 'deploy-frontend',
      category: 'deployment',
      title: 'Deploy Frontend to CloudFlare Pages',
      description: 'Host static site on CloudFlare',
      commands: [
        'cd apps/web',
        'npx wrangler pages deploy dist/',
        '# Or use CloudFlare Dashboard:',
        '# Pages → Create project',
        '# Upload dist folder',
        '# Set custom domain'
      ],
      duration: '10 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['frontend-build', 'frontend-api-integration'],
      outputs: ['Live website URL'],
      icon: GlobeAltIcon
    },
    {
      id: 'dns-setup',
      category: 'deployment',
      title: 'Configure DNS Records',
      description: 'Point domain to CloudFlare Pages',
      commands: [
        '# CloudFlare Dashboard → DNS',
        '# Add CNAME record:',
        '@ → happyhumantools.pages.dev',
        'www → happyhumantools.pages.dev',
        'api → happyhumantools-api.workers.dev'
      ],
      duration: '10 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['domain-purchase', 'deploy-frontend', 'deploy-worker'],
      outputs: ['Working domain'],
      icon: ServerIcon
    },

    // TESTING & LAUNCH
    {
      id: 'test-upload',
      category: 'testing',
      title: 'Test File Upload Flow',
      description: 'Verify complete upload process works',
      commands: [
        '# 1. Go to https://happyhumantools.com/send-email',
        '# 2. Enter business name: "Test Client"',
        '# 3. Enter email',
        '# 4. Send email',
        '# 5. Click link in email',
        '# 6. Upload a test file',
        '# 7. Verify file appears in R2 bucket'
      ],
      duration: '15 min',
      cost: '$0',
      required: true,
      status: 'pending',
      dependencies: ['dns-setup'],
      outputs: ['Verified working system'],
      icon: CheckCircleIcon
    },
    {
      id: 'monitoring',
      category: 'testing',
      title: 'Set Up Monitoring',
      description: 'Configure error tracking and analytics',
      commands: [
        '# CloudFlare Dashboard → Analytics',
        '# Enable Web Analytics',
        '# Workers → Your Worker → Logs',
        '# Set up error notifications',
        '# Optional: Add Sentry for error tracking'
      ],
      duration: '30 min',
      cost: '$0',
      required: false,
      status: 'pending',
      dependencies: ['test-upload'],
      outputs: ['Monitoring dashboard'],
      icon: WrenchIcon
    },
    {
      id: 'backup-strategy',
      category: 'testing',
      title: 'Configure Backups',
      description: 'Set up data backup strategy',
      commands: [
        '# CloudFlare R2 → Lifecycle rules',
        '# Enable versioning',
        '# D1 → Backups → Enable automatic backups',
        '# Download initial backup'
      ],
      duration: '20 min',
      cost: '$5/month',
      required: false,
      status: 'pending',
      dependencies: ['test-upload'],
      outputs: ['Backup system'],
      icon: ShieldCheckIcon
    }
  ];

  const getStepsByCategory = (category: string) => {
    if (category === 'all') return steps;
    return steps.filter(step => step.category === category);
  };

  const toggleStep = (stepId: string) => {
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  };

  const getTotalTime = () => {
    const times = steps.map(s => {
      if (s.duration.includes('hour')) {
        const hours = parseInt(s.duration) || 1;
        return hours * 60;
      }
      if (s.duration.includes('24h')) return 1440;
      return parseInt(s.duration) || 10;
    });
    const total = times.reduce((a, b) => a + b, 0);
    const hours = Math.floor(total / 60);
    const days = Math.floor(hours / 24);
    return `${days} days, ${hours % 24} hours`;
  };

  const getTotalCost = () => {
    const costs = [
      '$15/year (domain)',
      '$0.10/1000 emails',
      '$0.015/GB storage',
      '$5/month (optional backups)'
    ];
    return costs.join(' + ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-4">
            🚀 Complete Implementation Steps
          </h1>
          <p className="text-gray-400 text-lg">
            Every single step to make HappyHumanTools actually work in production
          </p>

          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-green-400">{steps.length}</div>
              <div className="text-sm text-gray-400">Total Steps</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-400">{getTotalTime()}</div>
              <div className="text-sm text-gray-400">Total Time</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold text-yellow-400">~$20/month</div>
              <div className="text-sm text-gray-400">Estimated Cost</div>
            </div>
          </div>
        </motion.div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeCategory === category.id
                  ? 'bg-white text-black font-semibold'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="space-y-4">
          <AnimatePresence>
            {getStepsByCategory(activeCategory).map((step, index) => {
              const Icon = step.icon;
              const isCompleted = completedSteps.has(step.id);
              const canStart = !step.dependencies ||
                step.dependencies.every(dep => completedSteps.has(dep));

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white/5 rounded-lg p-6 border ${
                    isCompleted ? 'border-green-500' :
                    canStart ? 'border-white/20' : 'border-red-500/30'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      isCompleted ? 'bg-green-500/20' : 'bg-white/10'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold">{step.title}</h3>
                        <div className="flex items-center gap-2">
                          {step.required && (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">
                              Required
                            </span>
                          )}
                          <button
                            onClick={() => toggleStep(step.id)}
                            disabled={!canStart}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              isCompleted
                                ? 'bg-green-500 text-white'
                                : canStart
                                  ? 'bg-white/20 hover:bg-white/30'
                                  : 'bg-red-500/20 text-red-400 cursor-not-allowed'
                            }`}
                          >
                            {isCompleted ? '✓ Completed' :
                             canStart ? 'Mark Complete' : 'Locked'}
                          </button>
                        </div>
                      </div>

                      <p className="text-gray-400 mb-4">{step.description}</p>

                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4 text-blue-400" />
                          <span className="text-sm">{step.duration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CreditCardIcon className="w-4 h-4 text-green-400" />
                          <span className="text-sm">{step.cost}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DocumentTextIcon className="w-4 h-4 text-purple-400" />
                          <span className="text-sm">Step {index + 1} of {steps.length}</span>
                        </div>
                      </div>

                      {step.dependencies && (
                        <div className="mb-4">
                          <div className="text-sm text-gray-500 mb-1">Dependencies:</div>
                          <div className="flex gap-2 flex-wrap">
                            {step.dependencies.map(dep => {
                              const depStep = steps.find(s => s.id === dep);
                              const isDepCompleted = completedSteps.has(dep);
                              return (
                                <span
                                  key={dep}
                                  className={`text-xs px-2 py-1 rounded ${
                                    isDepCompleted
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {isDepCompleted ? '✓' : '✗'} {depStep?.title}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {step.commands && (
                        <div className="bg-black/30 rounded-lg p-4 mb-4">
                          <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                            <CommandLineIcon className="w-4 h-4" />
                            Commands to run:
                          </div>
                          <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                            {step.commands.join('\n')}
                          </pre>
                        </div>
                      )}

                      {step.outputs && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500">Outputs:</span>
                          {step.outputs.map(output => (
                            <span
                              key={output}
                              className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded"
                            >
                              {output}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg p-6 border border-white/20"
        >
          <h2 className="text-2xl font-bold mb-4">📊 Implementation Summary</h2>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2">Infrastructure Needed:</h3>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• AWS Account (SES for emails)</li>
                <li>• CloudFlare Account (Workers, R2, D1)</li>
                <li>• Domain name</li>
                <li>• Credit card for services</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Monthly Costs:</h3>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• Domain: ~$1.25/month</li>
                <li>• R2 Storage: ~$5-10/month</li>
                <li>• SES Emails: ~$1/month</li>
                <li>• Workers: Free tier</li>
                <li className="font-semibold text-green-400">• Total: ~$10-20/month</li>
              </ul>
            </div>
          </div>

          <div className="mt-4 p-4 bg-black/30 rounded">
            <div className="text-sm text-gray-400 mb-2">Progress:</div>
            <div className="w-full bg-white/10 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all"
                style={{
                  width: `${(completedSteps.size / steps.length) * 100}%`
                }}
              />
            </div>
            <div className="text-right text-sm text-gray-400 mt-1">
              {completedSteps.size} of {steps.length} steps completed
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};